import { useEffect, useState } from 'react';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { Button, ButtonType } from '../../../../components/Button';
import { ModalBase } from './types';

// -- Basics and Bio Edit Modal ---------------------------------------------

const MONTHS_SHORT_BASICS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const ENGLISH_PROFICIENCIES = ['Native Speaker', 'Fluent', 'Proficient', 'Basic'];

const ABOUT_MAX = 2500;

const getDaysInMonth = (month: string, year: number): number[] => {
  const monthIdx = MONTHS_SHORT_BASICS.indexOf(month);
  if (monthIdx < 0) return Array.from({ length: 31 }, (_, i) => i + 1);
  const count = new Date(year || 2000, monthIdx + 1, 0).getDate();
  return Array.from({ length: count }, (_, i) => i + 1);
};

const parseDob = (dob?: string): { month: string; day: number; year: number } => {
  if (!dob) return { month: 'Jan', day: 1, year: 2000 };
  const d = new Date(dob.replace(' ', 'T'));
  if (isNaN(d.getTime())) return { month: 'Jan', day: 1, year: 2000 };
  return {
    month: MONTHS_SHORT_BASICS[d.getMonth()] ?? 'Jan',
    day: d.getDate(),
    year: d.getFullYear(),
  };
};

export const BasicsAndBioEditModal = ({
  isOpen,
  onClose,
  firstName,
  initialDob,
  initialEnglish,
  initialAbout,
  onSave,
}: ModalBase & {
  firstName?: string;
  initialDob?: string;
  initialEnglish?: string;
  initialAbout?: string;
  onSave: (data: { about: string; dob: string; english: string }) => Promise<void>;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const maxYear = new Date().getFullYear() - 18;

  const parsed = parseDob(initialDob);
  const [month, setMonth] = useState(parsed.month);
  const [day, setDay] = useState(parsed.day);
  const [year, setYear] = useState(String(parsed.year));
  const [english, setEnglish] = useState(initialEnglish ?? ENGLISH_PROFICIENCIES[0] ?? '');
  const [about, setAbout] = useState(initialAbout ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      const p = parseDob(initialDob);
      setMonth(p.month);
      setDay(p.day);
      setYear(String(p.year));
      setEnglish(initialEnglish ?? ENGLISH_PROFICIENCIES[0] ?? '');
      setAbout(initialAbout ?? '');
      setError('');
    }
  }, [isOpen, initialDob, initialEnglish, initialAbout]);

  const yearNum = Number(year);
  const monthIdx = MONTHS_SHORT_BASICS.indexOf(month);
  const now = new Date();
  const birthYearError = !!year && (yearNum < 1900 || yearNum > maxYear);
  const birthMonthError = !birthYearError && yearNum === maxYear && monthIdx > now.getMonth();
  const birthDayError =
    !birthYearError &&
    !birthMonthError &&
    yearNum === maxYear &&
    monthIdx === now.getMonth() &&
    day > now.getDate();

  const hasDateError = birthYearError || birthMonthError || birthDayError;

  const daysInMonth = getDaysInMonth(month, yearNum || 2000);

  const submit = async (): Promise<void> => {
    if (hasDateError) return;
    setSubmitting(true);
    try {
      const monthIdx2 = MONTHS_SHORT_BASICS.indexOf(month);
      const dobDate = new Date(yearNum, monthIdx2 < 0 ? 0 : monthIdx2, day);
      const mm = String(dobDate.getMonth() + 1).padStart(2, '0');
      const dd = String(dobDate.getDate()).padStart(2, '0');
      const dob = `${dobDate.getFullYear()}-${mm}-${dd} 00:00:00`;
      await onSave({ about, dob, english });
      onClose();
    } catch {
      setError(t('pages.sitterProfile.toasts.saveError'));
    } finally {
      setSubmitting(false);
    }
  };

  const remaining = ABOUT_MAX - about.length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pages.sitterProfile.index.editBasicsBioTitle')}
      className='w-full max-w-2xl'
      footer={
        <Button
          id='save-sitter-about-me'
          buttonType={ButtonType.primary}
          disabled={submitting || hasDateError}
          onClick={submit}
        >
          {t('common.save')}
        </Button>
      }
    >
      <div className='flex flex-wrap items-center gap-x-3 gap-y-2 mb-4'>
        <span className='font-semibold'>
          {t('pages.sitterProfile.modals.basicsAndBioModal.dateOfBirth')}
        </span>
        <select
          id='select-birth-month'
          aria-label={t('pages.sitterProfile.modals.basicsAndBioModal.birthMonthAriaLabel')}
          className='form-control border border-neutral-300 rounded pl-3 pr-8 py-2'
          value={month}
          onChange={(e): void => setMonth(e.target.value)}
        >
          {MONTHS_SHORT_BASICS.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        <select
          id='select-birth-day'
          aria-label={t('pages.sitterProfile.modals.basicsAndBioModal.birthDayAriaLabel')}
          className='form-control border border-neutral-300 rounded pl-3 pr-8 py-2'
          value={day}
          onChange={(e): void => setDay(Number(e.target.value))}
        >
          {daysInMonth.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
        <input
          id='input-birth-year'
          type='tel'
          aria-label={t('pages.sitterProfile.modals.basicsAndBioModal.birthYearAriaLabel')}
          placeholder={t('pages.sitterProfile.modals.basicsAndBioModal.birthYearPlaceholder')}
          className={`form-control border rounded px-3 py-2 w-24 ${hasDateError ? 'border-red-500' : 'border-neutral-300'}`}
          value={year}
          onChange={(e): void => setYear(e.target.value)}
        />
      </div>
      {hasDateError && (
        <p className='text-red-600 text-sm mb-3'>
          {t('pages.sitterProfile.modals.basicsAndBioModal.invalidBirthDate', { maxYear })}
        </p>
      )}

      <div className='flex flex-wrap items-center gap-3 mb-4'>
        <label htmlFor='select-english-proficiency' className='font-semibold'>
          {t('pages.sitterProfile.modals.basicsAndBioModal.englishProficiency')}
        </label>
        <select
          id='select-english-proficiency'
          className='form-control border border-neutral-300 rounded pl-3 pr-8 py-2'
          value={english}
          onChange={(e): void => setEnglish(e.target.value)}
        >
          {ENGLISH_PROFICIENCIES.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-4'>
        <div>
          <div className='flex justify-between items-center mb-1'>
            <h4 className='font-semibold m-0'>
              {t('pages.sitterProfile.index.sectionAbout', { name: firstName ?? '' })}
            </h4>
            <span className='text-xs italic text-neutral-500'>
              {t('pages.sitterProfile.modals.basicsAndBioModal.charactersRemaining', { count: remaining })}
            </span>
          </div>
          <textarea
            aria-label={t('pages.sitterProfile.index.sectionAbout', { name: firstName ?? '' })}
            className='form-control w-full border border-neutral-300 rounded p-2'
            rows={10}
            maxLength={ABOUT_MAX}
            value={about}
            onChange={(e): void => setAbout(e.target.value)}
          />
          <p className='text-sm font-semibold mt-1'>
            {t('pages.sitterProfile.modals.basicsAndBioModal.aboutOutsideBox')}
          </p>
        </div>
        <div className='text-sm'>
          <h4 className='font-semibold mb-2'>
            {t('pages.sitterProfile.modals.basicsAndBioModal.tipsHeading')}
          </h4>
          <ul className='list-disc pl-5 space-y-1 mb-4'>
            <li>{t('pages.sitterProfile.modals.basicsAndBioModal.tipsEducation')}</li>
            <li>{t('pages.sitterProfile.modals.basicsAndBioModal.tipsExperience')}</li>
            <li>{t('pages.sitterProfile.modals.basicsAndBioModal.tipsCaregiving')}</li>
            <li>{t('pages.sitterProfile.modals.basicsAndBioModal.tipsPersonal')}</li>
          </ul>
          <h4 className='font-semibold mb-2'>
            {t('pages.sitterProfile.modals.basicsAndBioModal.doNotHeading')}
          </h4>
          <ul className='list-disc pl-5 space-y-1'>
            <li>{t('pages.sitterProfile.modals.basicsAndBioModal.doNotLastName')}</li>
            <li>{t('pages.sitterProfile.modals.basicsAndBioModal.doNotContactInfo')}</li>
          </ul>
        </div>
      </div>
      {error && <p className='text-red-600 text-sm mt-3'>{error}</p>}
    </Modal>
  );
};
